import { DPR } from '@constants/common.const';
import { GameConfigService } from '@services/core/GameConfigService';

export type SpinnerConfig = {
  size?: number; // overall diameter in px
  spokes?: number; // default 12
  thickness?: number; // spoke thickness in px
  innerRadiusRatio?: number;
  length?: number; // spoke length in px (radial)
  inset?: number; // distance from outer edge inward
  color?: number; // 0xRRGGBB
  periodMs?: number; // full cycle duration, default 1000
  minAlpha?: number; // trailing min alpha
  maxAlpha?: number; // leading max alpha
};

export class Spinner extends Phaser.GameObjects.Container {
  private readonly cfg: Required<SpinnerConfig>;
  private spokes: Phaser.GameObjects.Rectangle[] = [];
  private tickEvent?: Phaser.Time.TimerEvent;
  private stepIndex = 0;

  // If you want to drive it from “progress” instead of time:
  private drivenByProgress = false;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    config: SpinnerConfig = {},
  ) {
    super(scene, x, y);

    this.cfg = {
      size: (config.size ?? 48) * DPR,
      spokes: config.spokes ?? 8,
      thickness: (config.thickness ?? 2) * DPR,
      length: (config.length ?? 10) * DPR,
      innerRadiusRatio: config.innerRadiusRatio ?? 0.5,
      inset: config.inset ?? Math.round((config.size ?? 50) * 0.08),
      color:
        config.color ?? new GameConfigService().getColor('--color-accent-2')!,
      periodMs: config.periodMs ?? 1000,
      minAlpha: config.minAlpha ?? 0.18,
      maxAlpha: config.maxAlpha ?? 1.0,
    };

    this.build();
    this.applyStep(this.stepIndex);

    this.setSize(this.cfg.size, this.cfg.size);
    this.setDepth(9999);
  }

  private build(): void {
    const outerRadius = this.cfg.size / 2;
    const innerRadius = outerRadius * this.cfg.innerRadiusRatio;

    const spokeW = this.cfg.thickness; // As requested
    const spokeH = this.cfg.length; // As requested

    // Position the spoke center between inner and outer radius
    const midRadius = innerRadius + spokeH / 2;

    for (let i = 0; i < this.cfg.spokes; i++) {
      const angle = (i * Phaser.Math.PI2) / this.cfg.spokes;

      // Calculate position
      const tx = Math.cos(angle - Math.PI / 2) * midRadius;
      const ty = Math.sin(angle - Math.PI / 2) * midRadius;

      // Rectangle avoids earcut re-triangulation every frame
      const rect = this.scene.add.rectangle(
        tx,
        ty,
        spokeW,
        spokeH,
        this.cfg.color,
      );
      rect.setRotation(angle);

      this.spokes.push(rect);
      this.add(rect);
    }
  }

  /** Start the stepped animation (like CSS: steps(12) over 1s). */
  start(): this {
    this.drivenByProgress = false;
    this.stop();

    const stepMs = this.cfg.periodMs / this.cfg.spokes;

    this.tickEvent = this.scene.time.addEvent({
      delay: stepMs,
      loop: true,
      callback: () => {
        this.stepIndex = (this.stepIndex + 1) % this.cfg.spokes;
        this.applyStep(this.stepIndex);
      },
    });

    return this;
  }

  /** Stop animation timer. */
  stop(): this {
    if (this.tickEvent) {
      this.tickEvent.remove(false);
      this.tickEvent = undefined;
    }
    return this;
  }

  /** Destroy safely. */
  override destroy(fromScene?: boolean): void {
    this.stop();
    super.destroy(fromScene);
  }

  /** Change base color (single color; segments are just different opacity). */
  setColor(color: number): this {
    this.cfg.color = color;
    for (const s of this.spokes) {
      s.setFillStyle(color);
    }
    return this;
  }

  /**
   * Drive the spinner highlight by “progress” (0..1) instead of time.
   * This is the simplest way to “step color dynamically” from your game state.
   */
  setProgress(progress01: number): this {
    this.drivenByProgress = true;

    const p = Phaser.Math.Clamp(progress01, 0, 1);
    const idx = Math.floor(p * this.cfg.spokes) % this.cfg.spokes;

    if (idx !== this.stepIndex) {
      this.stepIndex = idx;
      this.applyStep(this.stepIndex);
    }
    return this;
  }

  /**
   * If you want both: keep calling setProgress(), and it will override the time stepping.
   * If you want time stepping again, call start().
   */
  private applyStep(headIndex: number): void {
    // Make a “trail” by alpha only (no gradient).
    // head = maxAlpha, then fades down to minAlpha around the circle.
    const n = this.cfg.spokes;
    const maxA = this.cfg.maxAlpha;
    const minA = this.cfg.minAlpha;

    for (let i = 0; i < n; i++) {
      // distance behind head (0 = head, 1 = previous, ...)
      const behind = (headIndex - i + n) % n;

      // fade curve: fast drop early, slower later (looks close to CSS pseudo layers)
      const t = behind / (n - 1); // 0..1
      const curve = 1 - Math.pow(t, 0.75); // tweakable
      const alpha = minA + (maxA - minA) * curve;

      const s = this.spokes[i];
      s.setAlpha(alpha);
    }
  }
}
